import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useLocationStore } from '@/store/location';

// Types
export type TableShape = 'square' | 'round' | 'rectangle';
export type TableStatus = 'available' | 'occupied' | 'reserved' | 'blocked';

export interface Table {
  id: string;
  floor_plan_id: string;
  name: string;
  position_x: number;
  position_y: number;
  width: number;
  height: number;
  shape: TableShape;
  capacity: number;
  status: TableStatus;
  current_order_id: string | null;
  seated_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface FloorPlan {
  id: string;
  location_id: string;
  name: string;
  is_active: boolean;
  is_default: boolean;
  created_at: string;
  updated_at: string;
  tables?: Table[];
}

export interface TableWithOrder extends Table {
  order?: {
    id: string;
    order_number: string;
    status: string;
    total: number;
    created_at: string;
  } | null;
}

// Hook for managing floor plans and tables
export function useFloorPlans() {
  const queryClient = useQueryClient();
  const { currentLocation } = useLocationStore();
  const locationId = currentLocation?.id;

  // Fetch all floor plans for the location
  const {
    data: floorPlans,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['floor-plans', locationId],
    queryFn: async () => {
      if (!locationId) return [];

      const { data, error } = await supabase
        .from('floor_plans')
        .select(`
          *,
          tables(*)
        `)
        .eq('location_id', locationId)
        .order('is_default', { ascending: false })
        .order('name');

      if (error) throw error;

      return (data as FloorPlan[]).map(fp => ({
        ...fp,
        tables: fp.tables?.sort((a, b) => a.name.localeCompare(b.name)),
      }));
    },
    enabled: !!locationId,
  });

  // Get the default floor plan
  const defaultFloorPlan = floorPlans?.find(fp => fp.is_default) || floorPlans?.[0];

  // Create a new floor plan
  const createFloorPlan = useMutation({
    mutationFn: async ({ name, isDefault = false }: { name: string; isDefault?: boolean }) => {
      if (!locationId) throw new Error('No location selected');

      // If setting as default, unset other defaults first
      if (isDefault) {
        await supabase
          .from('floor_plans')
          .update({ is_default: false })
          .eq('location_id', locationId);
      }

      const { data, error } = await supabase
        .from('floor_plans')
        .insert({
          location_id: locationId,
          name,
          is_active: true,
          is_default: isDefault,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['floor-plans', locationId] });
    },
  });

  // Update a floor plan
  const updateFloorPlan = useMutation({
    mutationFn: async ({
      id,
      name,
      isActive,
      isDefault,
    }: {
      id: string;
      name?: string;
      isActive?: boolean;
      isDefault?: boolean;
    }) => {
      // If setting as default, unset other defaults first
      if (isDefault) {
        await supabase
          .from('floor_plans')
          .update({ is_default: false })
          .eq('location_id', locationId);
      }

      const updates: Record<string, unknown> = { updated_at: new Date().toISOString() };
      if (name !== undefined) updates.name = name;
      if (isActive !== undefined) updates.is_active = isActive;
      if (isDefault !== undefined) updates.is_default = isDefault;

      const { data, error } = await supabase
        .from('floor_plans')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['floor-plans', locationId] });
    },
  });

  // Delete a floor plan
  const deleteFloorPlan = useMutation({
    mutationFn: async (id: string) => {
      // First delete all tables
      await supabase.from('tables').delete().eq('floor_plan_id', id);

      // Then delete the floor plan
      const { error } = await supabase.from('floor_plans').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['floor-plans', locationId] });
    },
  });

  // Create a new table
  const createTable = useMutation({
    mutationFn: async ({
      floorPlanId,
      name,
      positionX = 100,
      positionY = 100,
      width = 80,
      height = 80,
      shape = 'square' as TableShape,
      capacity = 4,
    }: {
      floorPlanId: string;
      name: string;
      positionX?: number;
      positionY?: number;
      width?: number;
      height?: number;
      shape?: TableShape;
      capacity?: number;
    }) => {
      const { data, error } = await supabase
        .from('tables')
        .insert({
          floor_plan_id: floorPlanId,
          name,
          position_x: positionX,
          position_y: positionY,
          width,
          height,
          shape,
          capacity,
          status: 'available',
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['floor-plans', locationId] });
    },
  });

  // Update a table
  const updateTable = useMutation({
    mutationFn: async ({
      id,
      name,
      positionX,
      positionY,
      width,
      height,
      shape,
      capacity,
      status,
    }: {
      id: string;
      name?: string;
      positionX?: number;
      positionY?: number;
      width?: number;
      height?: number;
      shape?: TableShape;
      capacity?: number;
      status?: TableStatus;
    }) => {
      const updates: Record<string, unknown> = { updated_at: new Date().toISOString() };
      if (name !== undefined) updates.name = name;
      if (positionX !== undefined) updates.position_x = positionX;
      if (positionY !== undefined) updates.position_y = positionY;
      if (width !== undefined) updates.width = width;
      if (height !== undefined) updates.height = height;
      if (shape !== undefined) updates.shape = shape;
      if (capacity !== undefined) updates.capacity = capacity;
      if (status !== undefined) updates.status = status;

      const { data, error } = await supabase
        .from('tables')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['floor-plans', locationId] });
    },
  });

  // Delete a table
  const deleteTable = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('tables').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['floor-plans', locationId] });
    },
  });

  // Assign an order to a table
  const assignOrderToTable = useMutation({
    mutationFn: async ({ tableId, orderId }: { tableId: string; orderId: string | null }) => {
      const updates: Record<string, unknown> = {
        current_order_id: orderId,
        status: orderId ? 'occupied' : 'available',
        seated_at: orderId ? new Date().toISOString() : null,
        updated_at: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from('tables')
        .update(updates)
        .eq('id', tableId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['floor-plans', locationId] });
    },
  });

  // Set table status (reserved, blocked, etc.)
  const setTableStatus = useMutation({
    mutationFn: async ({ tableId, status }: { tableId: string; status: TableStatus }) => {
      const { data, error } = await supabase
        .from('tables')
        .update({
          status,
          updated_at: new Date().toISOString(),
          // Clear order if setting to available
          ...(status === 'available' ? { current_order_id: null, seated_at: null } : {}),
        })
        .eq('id', tableId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['floor-plans', locationId] });
    },
  });

  return {
    floorPlans: floorPlans ?? [],
    defaultFloorPlan,
    isLoading,
    error,
    refetch,
    createFloorPlan,
    updateFloorPlan,
    deleteFloorPlan,
    createTable,
    updateTable,
    deleteTable,
    assignOrderToTable,
    setTableStatus,
  };
}

// Hook for getting tables with their current orders
export function useTablesWithOrders(floorPlanId: string | null) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['tables-with-orders', floorPlanId],
    queryFn: async () => {
      if (!floorPlanId) return [];

      const { data, error } = await supabase
        .from('tables')
        .select(`
          *,
          order:orders(
            id,
            order_number,
            status,
            total,
            created_at
          )
        `)
        .eq('floor_plan_id', floorPlanId)
        .order('name');

      if (error) throw error;

      return data.map(table => ({
        ...table,
        order: table.order?.[0] || null,
      })) as TableWithOrder[];
    },
    enabled: !!floorPlanId,
    refetchInterval: 10000, // Refresh every 10 seconds
  });

  return {
    tables: data ?? [],
    isLoading,
    error,
    refetch,
  };
}
